interface FavoriteThumbnailItem {
  game_name: string;
  game_japanese_name: string | null;
  game_cover: string | null;
}

const getCoverUrl = (coverId: string | null): string => {
  if (!coverId) return '';
  if (coverId.startsWith('pulse_')) {
    return `https://plsdb.com/cover/${coverId.replace('pulse_', '')}.jpg`;
  }
  return `https://images.igdb.com/igdb/image/upload/t_cover_big/${coverId}.jpg`;
};

const loadImage = (src: string): Promise<HTMLImageElement | null> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
};

const drawRoundedPath = (
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, radius: number,
) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

export const generateFavoritesThumbnail = async (
  items: FavoriteThumbnailItem[],
  displayName: string,
): Promise<Blob | null> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // OGP推奨サイズ 1.91:1
  canvas.width = 1200;
  canvas.height = 630;

  // グラデーション背景
  const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, '#0F0F13');
  gradient.addColorStop(0.4, '#151620');
  gradient.addColorStop(1, '#1A1B25');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // アクセントグラデーション
  const accentGradient1 = ctx.createRadialGradient(0, 0, 0, 0, 0, canvas.width);
  accentGradient1.addColorStop(0, 'rgba(190, 255, 0, 0.15)');
  accentGradient1.addColorStop(0.3, 'rgba(190, 255, 0, 0.05)');
  accentGradient1.addColorStop(1, 'rgba(190, 255, 0, 0)');
  ctx.fillStyle = accentGradient1;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const accentGradient2 = ctx.createRadialGradient(
    canvas.width, canvas.height, 0,
    canvas.width, canvas.height, canvas.width * 0.8,
  );
  accentGradient2.addColorStop(0, 'rgba(0, 200, 255, 0.1)');
  accentGradient2.addColorStop(0.4, 'rgba(0, 200, 255, 0.03)');
  accentGradient2.addColorStop(1, 'rgba(0, 200, 255, 0)');
  ctx.fillStyle = accentGradient2;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // ノイズテクスチャ
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const noise = Math.random() * 2;
    data[i] = Math.min(data[i] + noise, 255);
    data[i + 1] = Math.min(data[i + 1] + noise, 255);
    data[i + 2] = Math.min(data[i + 2] + noise, 255);
  }
  ctx.putImageData(imageData, 0, 0);

  const contentPadding = 50;
  const contentWidth = canvas.width - contentPadding * 2;

  // ヘッダー1行: タイトル左 + ロゴ右
  const headerY = 40;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
  ctx.font = 'bold 24px "Helvetica Neue", Arial, "Hiragino Kaku Gothic ProN", "Hiragino Sans", sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  const titleText = `${displayName}'s Best Games`;
  const maxTitleWidth = contentWidth - 150;
  if (ctx.measureText(titleText).width > maxTitleWidth) {
    let truncated = displayName;
    while (ctx.measureText(truncated + `...'s Best Games`).width > maxTitleWidth && truncated.length > 0) {
      truncated = truncated.slice(0, -1);
    }
    ctx.fillText(`${truncated}...'s Best Games`, contentPadding, headerY);
  } else {
    ctx.fillText(titleText, contentPadding, headerY);
  }

  const logo = await loadImage('/pulse_logo.png');
  if (logo) {
    const logoHeight = 24;
    const logoWidth = (logo.width / logo.height) * logoHeight;
    ctx.drawImage(logo, canvas.width - logoWidth - contentPadding, headerY - logoHeight / 2, logoWidth, logoHeight);
  }

  // グリッドレイアウト: 全件表示、列数は件数に応じて可変
  // ≤8→4列, 9-10→5列, 11-12→6列, 13-14→7列, 15-16→8列
  const cols = Math.ceil(items.length / 2);
  const clampedCols = Math.max(4, Math.min(cols, 8));
  const displayCount = items.length;
  const rowCount = displayCount <= clampedCols ? 1 : 2;

  const gap = 18;
  const gridTop = 68;
  const gridBottom = 20;
  const availableHeight = canvas.height - gridTop - gridBottom;

  let coverH = rowCount === 1
    ? Math.min(availableHeight, 280)
    : (availableHeight - gap) / 2;
  let coverW = coverH * (3 / 4);
  let gridWidth = coverW * clampedCols + gap * (clampedCols - 1);

  // 幅に収まらない場合は幅基準で計算
  if (gridWidth > contentWidth) {
    coverW = (contentWidth - gap * (clampedCols - 1)) / clampedCols;
    coverH = coverW * (4 / 3);
    gridWidth = contentWidth;
  }

  // グリッド実サイズから上下中央配置
  const gridHeight = coverH * rowCount + gap * (rowCount - 1);
  const startY = gridTop + (availableHeight - gridHeight) / 2;
  const startX = contentPadding + (contentWidth - gridWidth) / 2;

  for (let i = 0; i < displayCount; i++) {
    const item = items[i];
    const coverUrl = getCoverUrl(item.game_cover);
    if (!coverUrl) continue;

    const cover = await loadImage(coverUrl);
    if (!cover) continue;

    const col = i % clampedCols;
    const row = Math.floor(i / clampedCols);
    const x = startX + col * (coverW + gap);
    const y = startY + row * (coverH + gap);

    ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
    ctx.shadowBlur = 16;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 6;

    ctx.save();
    drawRoundedPath(ctx, x, y, coverW, coverH, 6);
    ctx.clip();
    ctx.drawImage(cover, x, y, coverW, coverH);
    ctx.restore();
  }

  // シャドウリセット
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.85);
  });
};
